import React from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { collectDroppedFiles } from '../utils/droppedFiles.js';
import { selectImportRange } from '../utils/importRange.js';
import { prepareImportSelection } from '../utils/importSelection.js';
import ImportRangeDialog from './ImportRangeDialog.jsx';

export default function FileSelection({ onSelect, onClear, onCancel, busy, hasImports }) {
  const fileInput = React.useRef(null);
  const folderInput = React.useRef(null);
  const scanning = React.useRef(false);
  const mounted = React.useRef(true);
  const dragDepth = React.useRef(0);
  const [folderSupported, setFolderSupported] = React.useState(false);
  const [dragging, setDragging] = React.useState(false);
  const [reading, setReading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [pending, setPending] = React.useState(null);
  const disabled = busy || reading || Boolean(pending);
  React.useEffect(() => {
    mounted.current = true;
    setFolderSupported('webkitdirectory' in document.createElement('input'));
    // Prevent an accidental file drop outside the zone from replacing the app.
    const preventFileNavigation = (event) => {
      if (Array.from(event.dataTransfer?.types || []).includes('Files')) event.preventDefault();
    };
    window.addEventListener('dragover', preventFileNavigation);
    window.addEventListener('drop', preventFileNavigation);
    return () => {
      mounted.current = false;
      window.removeEventListener('dragover', preventFileNavigation);
      window.removeEventListener('drop', preventFileNavigation);
    };
  }, []);
  const handlePrepare = async (selectionPromise) => {
    scanning.current = true;
    setReading(true);
    setError('');
    try {
      const selection = await selectionPromise;
      const prepared = await prepareImportSelection(selection);
      if (mounted.current) {
        if (!prepared.files.length)
          setError('No SCP files found in the selected files, folders or ZIP archives.');
        else setPending(prepared);
      }
    } catch (readError) {
      if (mounted.current)
        setError(`Nothing imported: ${readError.message || 'Unable to read the selection.'}`);
    } finally {
      scanning.current = false;
      if (mounted.current) setReading(false);
    }
  };
  const handleChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!disabled && !scanning.current && files.length) handlePrepare(Promise.resolve(files));
  };
  const handleDismiss = () => setPending(null);
  const handleImport = (range) => {
    if (!pending || busy) return;
    const selection = selectImportRange(pending, range);
    const info = {
      skipped: pending.skipped,
      available: pending.files.length,
      rangeStart: range.start,
      rangeEnd: range.end,
    };
    setPending(null);
    onSelect(selection, info);
  };
  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current += 1;
    if (!disabled) setDragging(true);
  };
  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDragging(false);
  };
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
  };
  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = 0;
    setDragging(false);
    if (disabled || scanning.current) return;
    // Entries must be captured while the drop event still owns DataTransfer.
    handlePrepare(collectDroppedFiles(event.dataTransfer));
  };
  return (
    <Box>
      <Box
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          p: 3,
          mb: 2,
          textAlign: 'center',
          border: '2px dashed',
          borderColor: dragging ? 'primary.main' : 'divider',
          bgcolor: dragging ? 'action.hover' : 'background.paper',
          borderRadius: 2,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Typography variant="h6">Drop ECG files, folders or ZIP archives here</Typography>
        <Typography aria-live="polite">
          {reading
            ? 'Finding SCP files…'
            : busy
              ? 'Import in progress…'
              : 'Drag from Windows Explorer, then choose how many SCP files to import.'}
        </Typography>
      </Box>
      <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
        <Button variant="contained" disabled={disabled} onClick={() => fileInput.current.click()}>
          Select SCP / ZIP files
        </Button>
        <Button
          variant="outlined"
          disabled={disabled || !folderSupported}
          onClick={() => folderInput.current.click()}
        >
          Import folder
        </Button>
        {busy && <Button onClick={onCancel}>Cancel import</Button>}
        <Button disabled={disabled || !hasImports} onClick={onClear}>
          Clear session
        </Button>
      </Stack>
      <input
        hidden
        ref={fileInput}
        type="file"
        multiple
        accept=".scp,.zip"
        onChange={handleChange}
      />
      <input
        hidden
        ref={folderInput}
        type="file"
        multiple
        webkitdirectory=""
        directory=""
        onChange={handleChange}
      />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {!folderSupported && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Folder selection is unavailable. Use Import SCP files.
        </Alert>
      )}
      <Typography variant="body2" sx={{ mt: 2 }}>
        ZIP archives and subfolders are searched for SCP files. Choose a From/To range of up to
        10,000 files before upload starts. Positions use natural folder/filename order; the viewer
        then orders recordings by their embedded timestamps. Recordings stay available until you
        clear or leave this page.
      </Typography>
      <ImportRangeDialog
        open={Boolean(pending)}
        fileCount={pending?.files.length || 0}
        skipped={pending?.skipped || 0}
        onConfirm={handleImport}
        onClose={handleDismiss}
      />
    </Box>
  );
}
FileSelection.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  busy: PropTypes.bool.isRequired,
  hasImports: PropTypes.bool.isRequired,
};
